import React, { useState, useEffect } from "react";
import { Users, Plus, Trash2, Edit } from "lucide-react";
import {
  getPartnerUsers,
  createPartnerUser,
  deletePartnerUser,
  updatePartnerUser,
  PartnerUser,
} from "../services/partnerUser.ts";
import {
  getMyOrganizations,
  getUserOrgPermissions,
  OrgWithPermission,
} from "../services/organization.ts";
import messageService from "../components/Message/message.ts";
import ConfirmModal from "../components/Modals/ConfirmModal.tsx";
import AddUserModal from "../components/Modals/AddUserModal.tsx";

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationIds: string[];
}

const UserSettings: React.FC = () => {
  const [users, setUsers] = useState<PartnerUser[]>([]);
  const [organizations, setOrganizations] = useState<OrgWithPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: "",
    firstName: "",
    lastName: "",
    role: "Partner Tech",
    organizationIds: [],
  });
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUserEmail, setDeleteUserEmail] = useState<string | null>(null);

  const roleOptions = [
    { value: "Partner Admin", label: "Partner Admin" },
    { value: "Partner Tech", label: "Partner Tech" },
    { value: "Partner Viewer", label: "Partner Viewer" },
  ];

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getPartnerUsers();
      setUsers(usersData);
    } catch (err) {
      messageService.error("Failed to fetch users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const organizationsData = await getMyOrganizations();
      setOrganizations(organizationsData);
    } catch (err) {
      messageService.error("Failed to fetch organizations");
      console.error("Error fetching organizations:", err);
    }
  };

  const handleAddUser = async (formData: UserFormData) => {
    try {
      await createPartnerUser(formData);
      messageService.success(`User invitation sent to ${formData.email}`);
      setShowAddModal(false);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      messageService.error(
        err.response?.data?.message || "Failed to create user"
      );
    }
  };

  const handleEditUser = async (formData: UserFormData) => {
    if (!editingUserId) return;

    try {
      await updatePartnerUser(editingUserId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        organizationIds: formData.organizationIds,
      });
      messageService.success("User updated successfully");
      setShowEditModal(false);
      setEditingUserId(null);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      messageService.error(
        err.response?.data?.message || "Failed to update user"
      );
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    try {
      await deletePartnerUser(userId);
      messageService.success("User deleted successfully");
      setDeleteUserId(null);
      setDeleteUserEmail(null);
      fetchUsers();
    } catch (err: any) {
      messageService.error(
        err.response?.data?.message || "Failed to delete user"
      );
      setDeleteUserId(null);
      setDeleteUserEmail(null);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      role: "Partner Tech",
      organizationIds: [],
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleColor = (role: string | null) => {
    if (!role) {
      return "bg-gray-100 text-gray-800"; // Default color for null/undefined roles
    }

    switch (role.toLowerCase()) {
      case "admin":
        return "bg-purple-100 text-purple-800";
      case "user":
        return "bg-blue-100 text-blue-800";
      case "partner tech":
        return "bg-green-100 text-green-800";
      case "partner admin":
        return "bg-orange-100 text-orange-800";
      case "partner viewer":
        return "bg-cyan-100 text-cyan-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const onEditClick = async (user: PartnerUser) => {
    try {
      // 获取用户的组织权限
      const userOrgPermissions = await getUserOrgPermissions(user.id);

      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role || "Partner Tech", // Provide default role if user.role is null/undefined
        organizationIds: userOrgPermissions.data.map((org) => org.id), // 使用API返回的组织ID列表
      });
      setEditingUserId(user.id);
      setShowEditModal(true);
    } catch (err: any) {
      messageService.error(
        err.response?.data?.message || "Failed to load user organizations"
      );
      console.error("Error fetching user organizations:", err);
    }
  };

  const onDeleteClick = (userId: string, userEmail: string) => {
    setDeleteUserId(userId);
    setDeleteUserEmail(userEmail);
  };

  if (loading) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Settings</h1>
            <p className="text-lg text-gray-600 mt-2">
              Manage users and their permissions
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          </div>

          {users.length === 0 ? (
            <div className="p-8">
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No Users Found
                </h3>
                <p className="text-gray-600 mb-6">
                  Get started by adding your first user to the system
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First User
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {user.firstName.charAt(0)}
                                {user.lastName.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(
                            user.role
                          )}`}
                        >
                          {user.role || "No Role"}{" "}
                          {/* Also handle display of null roles */}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            user.status
                          )}`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-1">
                          <button
                            onClick={() => onEditClick(user)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit user"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDeleteClick(user.id, user.email)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        onSubmit={handleAddUser}
        formData={formData}
        setFormData={setFormData}
        roleOptions={roleOptions}
        organizations={organizations}
        isEditMode={false}
      />

      {/* Edit User Modal */}
      <AddUserModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingUserId(null);
          resetForm();
        }}
        onSubmit={handleEditUser}
        formData={formData}
        setFormData={setFormData}
        roleOptions={roleOptions}
        organizations={organizations}
        isEditMode={true}
        editingUserId={editingUserId || undefined}
      />

      <ConfirmModal
        open={!!deleteUserId}
        title="Confirm Delete"
        description={`Are you sure you want to delete ${deleteUserEmail}?`}
        onCancel={() => {
          setDeleteUserId(null);
          setDeleteUserEmail(null);
        }}
        onConfirm={() => handleDeleteUser(deleteUserId!, deleteUserEmail!)}
      />
    </div>
  );
};

export default UserSettings;
